import type { BoardDoc } from '../doc/BoardDoc';
import { DEG, type Point } from '../doc/geometry';
import { drawFrame, drawShape, highlighterBlend } from './export/rasterize';
import { paintStroke } from './items/StrokeItem';
import { labelLookOf, paintConnector } from './items/ConnectorItem';

/**
 * The colour picker's sampler: the colour of the board at a world point, as it looks in the pane.
 * The items under the point are drawn into a one-pixel canvas, bottom to top, over the canvas
 * background. Images come from their loaded <img> elements, sketch sheets from their layers as
 * the pane shows them, ink, shapes and frames are drawn as the exporter draws them, and
 * text-based items count as their background colour.
 * Synchronous, so it can follow the pointer.
 */
export function sampleColor(board: BoardDoc, viewport: HTMLElement, world: Point, zoom: number): string | null {
  // one canvas pixel covers what one device pixel shows
  const scale = zoom * (window.devicePixelRatio || 1);
  // a fresh canvas each time: one that an image ever tainted could never be read again
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const vs = getComputedStyle(viewport);
  paintBox(ctx, vs.backgroundColor, 1, 1);
  ctx.setTransform(scale, 0, 0, scale, 0.5 - world.x * scale, 0.5 - world.y * scale);
  const half = 0.5 / scale;
  const items = board.index.query({ x: world.x - half, y: world.y - half, w: half * 2, h: half * 2 });
  const lineColor = vs.getPropertyValue('--line').trim() || '#888888';
  const labelLook = labelLookOf(viewport, vs.backgroundColor);
  const blend = highlighterBlend();
  for (const item of items) {
    if (board.isHidden(item)) continue;
    const el = viewport.querySelector<HTMLElement>(`[data-item-id="${item.id}"]`);
    ctx.save();
    ctx.translate(item.x + item.w / 2, item.y + item.h / 2);
    ctx.rotate((item.rotation || 0) * DEG);
    ctx.translate(-item.w / 2, -item.h / 2);
    switch (item.type) {
      case 'image': {
        const img = el?.querySelector('img');
        if (img?.complete && img.naturalWidth) {
          ctx.globalAlpha = item.props.opacity;
          ctx.drawImage(img, 0, 0, item.w, item.h);
        }
        break;
      }
      case 'shape':
        drawShape(ctx, item);
        break;
      case 'frame':
        drawFrame(ctx, item, lineColor);
        break;
      case 'stroke':
        paintStroke(ctx, item, blend);
        break;
      case 'connector':
        paintConnector(ctx, item, board.index.connectorGeometry(item), labelLook);
        break;
      case 'sheet':
        // the paper, then each visible layer as the pane shows it: its <img>, or its live canvas
        paintBox(ctx, item.props.paper, item.w, item.h);
        for (const layer of el?.querySelectorAll<HTMLElement>('.sheet-layer') ?? []) {
          ctx.globalAlpha = Number(layer.style.opacity || 1);
          const img = layer.querySelector('img');
          const live = layer.querySelector('canvas');
          if (img?.complete && img.naturalWidth) ctx.drawImage(img, 0, 0, item.w, item.h);
          if (live?.width && live.height) ctx.drawImage(live, 0, 0, item.w, item.h);
        }
        break;
      default:
        if (el) paintBox(ctx, backgroundOf(el), item.w, item.h);
    }
    ctx.restore();
  }
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, 1, 1).data;
  } catch {
    return null; // tainted by an image from elsewhere
  }
  if (!data[3]) return null;
  return '#' + [data[0]!, data[1]!, data[2]!].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function paintBox(ctx: CanvasRenderingContext2D, color: string | null, w: number, h: number): void {
  if (!color) return;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, w, h);
}

/** A text item's paper: its own background, else that of its body. */
function backgroundOf(el: HTMLElement): string | null {
  for (const e of [el, el.firstElementChild]) {
    if (!(e instanceof HTMLElement)) continue;
    const bg = getComputedStyle(e).backgroundColor;
    if (bg && bg !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(bg)) return bg;
  }
  return null;
}
